import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@parcel/ui';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { apiErrorCode } from '../../lib/api.js';
import {
  type SeatChangePreview,
  previewWorkspaceSeats,
  setWorkspaceSeats,
} from '../../lib/billing.js';
import { specBillingSaveMessage, updateSpecSeatQuantity } from '../../lib/spec-billing.js';
import {
  isCommitDisabled,
  isNoChange,
  isPreviewReady,
  previewErrorMessage,
  shouldRebaselineTarget,
} from './manage-seats-state.js';
import { SpecSeatQuantitySection } from './spec-seat-quantity-section.js';

// Mirrors the server's MAX_EXTRA_SEATS (apps/api/src/routes/billing.ts). Caps the stepper so it
// can't exceed the server limit, which would otherwise surface as a misleading preview error.
const MAX_EXTRA_SEATS = 1000;

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

export interface ManageSeatsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  /** Base seats included with the plan (e.g. Pro = 10). */
  includedSeats: number;
  /** Current EXTRA seats beyond the plan base. */
  currentQuantity: number;
  /** Called after a successful commit (or a stale-count error) so the parent can refresh. */
  onCommitted: () => void | Promise<void>;
  /**
   * Whether the WORKSPACE-seat half applies at all. False on a plan that cannot
   * buy extra seats, which is every plan below Pro.
   *
   * Defaults true, so callers that only ever mount this for a Pro workspace do
   * not have to say so. Passing false hides that half AND suppresses its
   * preview: `GET /seats/preview` answers 403 `seats_not_pro` on those plans, so
   * issuing it would put a permanent error banner over a dialog whose other
   * half works fine.
   */
  canManageWorkspaceSeats?: boolean;
}

/**
 * Pick a target seat count, preview the exact prorated charge and new monthly total
 * (read-only, no charge), then commit once. Replaces the per-click stepper, which
 * billed a separate proration on every increment.
 */
export function ManageSeatsDialog({
  open,
  onOpenChange,
  workspaceId,
  includedSeats,
  currentQuantity,
  onCommitted,
  canManageWorkspaceSeats = true,
}: ManageSeatsDialogProps) {
  const [target, setTarget] = React.useState(currentQuantity);
  const [preview, setPreview] = React.useState<SeatChangePreview | null>(null);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [previewError, setPreviewError] = React.useState<string | null>(null);
  // Bumped to force a fresh preview fetch, e.g. after a 409 invalidates the baseline.
  const [reloadKey, setReloadKey] = React.useState(0);
  // AI seats. `null` until the Spec summary lands, or forever when Spec is not
  // visible for this workspace, which is how the commit below knows to leave
  // that half alone entirely rather than writing a zero over it.
  const queryClient = useQueryClient();
  const [aiTarget, setAiTarget] = React.useState<number | null>(null);
  const [aiBaseline, setAiBaseline] = React.useState<number | null>(null);
  // True once the user has moved the stepper this session, so the one-shot re-baseline to
  // Stripe's live count (below) only fires before they start adjusting.
  const userAdjustedRef = React.useRef(false);
  // The AI half's equivalent: whether the manager has moved the AI stepper in
  // THIS open session, which decides if a later baseline may re-seed it.
  const aiUserAdjustedRef = React.useRef(false);
  const wasOpenRef = React.useRef(false);
  // Latest onCommitted in a ref so the preview effect can call it (best-effort parent refresh on
  // drift) without listing it as a dependency, which would re-run the preview on every parent
  // render.
  const onCommittedRef = React.useRef(onCommitted);
  React.useEffect(() => {
    onCommittedRef.current = onCommitted;
  });

  // Full-reset only when the dialog TRANSITIONS to open, clearing any cached preview from a
  // previous session. useLayoutEffect runs before paint, so a reopen can't briefly show last
  // session's target with a commit-ready button. Gating on the transition (not every
  // currentQuantity change) also keeps a post-409 parent refresh from wiping the
  // seats_out_of_sync error before the user reads it.
  React.useLayoutEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (justOpened) {
      setTarget(currentQuantity);
      setPreview(null);
      setPreviewError(null);
      setError(null);
      setLoadingPreview(true);
      setAiTarget(null);
      setAiBaseline(null);
      userAdjustedRef.current = false;
      aiUserAdjustedRef.current = false;
    }
  }, [open, currentQuantity]);

  // Debounced, read-only cost preview on every target change. Never charges.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reloadKey is a manual refetch trigger (bumped on 409); intentionally not read in the body.
  React.useEffect(() => {
    if (!open) return;
    // Nothing to price, and the route would 403 `seats_not_pro`. Clearing the
    // loading flag matters: it gates the commit button, so leaving it true
    // would make an AI-only dialog permanently unsubmittable.
    if (!canManageWorkspaceSeats) {
      setLoadingPreview(false);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setPreviewError(null);
    const timer = setTimeout(() => {
      previewWorkspaceSeats(workspaceId, target)
        .then((next) => {
          if (cancelled) return;
          setPreview(next);
          setPreviewError(null);
          // Stripe is the source of truth. If the DB-sourced prop was stale, snap the stepper
          // to Stripe's live count once, before the user has adjusted, so they see and change
          // from the real current rather than a stale number.
          if (shouldRebaselineTarget(userAdjustedRef.current, next, target)) {
            setTarget(next.currentQuantity);
            // The DB-sourced parent counts were stale; refresh them (best-effort) so the page
            // behind the modal isn't left showing pre-drift seat totals.
            void onCommittedRef.current();
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setPreview(null);
          setPreviewError(apiErrorCode(err) ?? 'unreachable');
        })
        .finally(() => {
          if (!cancelled) setLoadingPreview(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, target, workspaceId, reloadKey, canManageWorkspaceSeats]);

  // Stable so the section's effect does not re-fire every render.
  //
  // A later report -- a refetch landing after a cached read seeded the first
  // one -- moves the target too, UNLESS the manager has already moved the
  // stepper. Preserving the target unconditionally meant a stale cached value
  // could seed it, the refetch could then correct only the baseline, and the
  // dialog would sit there offering to "change" the quantity back to the stale
  // number, with the confirm button live. Mirrors `userAdjustedRef` on the
  // workspace-seat half, which rebaselines for the same reason.
  const handleAiBaseline = React.useCallback((next: { quantity: number; assigned: number }) => {
    setAiBaseline(next.quantity);
    setAiTarget((current) => (aiUserAdjustedRef.current ? current : next.quantity));
  }, []);

  const handleAiTargetChange = React.useCallback((next: number) => {
    aiUserAdjustedRef.current = true;
    setAiTarget(next);
  }, []);

  const currency = preview?.currency ?? 'usd';
  const dueTodayCents = preview?.dueTodayCents ?? 0;
  const newMonthlyTotalCents = preview?.newMonthlyTotalCents ?? null;
  const workspaceSeatsChanged =
    canManageWorkspaceSeats && !isNoChange(target, preview, currentQuantity);
  const aiSeatsChanged = aiTarget !== null && aiBaseline !== null && aiTarget !== aiBaseline;
  const noChange = !workspaceSeatsChanged && !aiSeatsChanged;
  // The preview only gates the WORKSPACE half. An AI seat's price is on the
  // Spec summary rather than in Stripe, so a preview outage must not block a
  // change that never needed one.
  // The helper only cares WHETHER the preview failed; the code is for the message.
  const previewReady = workspaceSeatsChanged
    ? isPreviewReady(target, preview, previewError !== null)
    : true;

  const dueLabel =
    dueTodayCents > 0
      ? formatMoney(dueTodayCents, currency)
      : dueTodayCents < 0
        ? `${formatMoney(Math.abs(dueTodayCents), currency)} credit`
        : formatMoney(0, currency);

  const primaryLabel = noChange
    ? 'No changes'
    : dueTodayCents > 0 && !loadingPreview
      ? `Confirm & pay ${formatMoney(dueTodayCents, currency)}`
      : 'Update seats';

  /**
   * Commits whichever seat kinds actually changed, each on its own.
   *
   * Deliberately NOT one transaction. They are two unrelated Stripe writes, and
   * wrapping them would mean a failure on one silently discarding a valid
   * change to the other -- worse, it would mean reporting "no change made"
   * after one of them already charged a card. So each is attempted, each
   * reports its own outcome, and the dialog stays open if either failed so the
   * remaining half can be retried against a state the manager can still see.
   */
  const commit = async () => {
    setSaving(true);
    setError(null);
    const failures: string[] = [];
    let staleWorkspaceSeats = false;

    if (workspaceSeatsChanged) {
      // Commit is gated on previewReady, so preview is loaded here. Use ITS currentQuantity
      // (read from Stripe) as the known baseline, not the DB-sourced prop: that way the change
      // the user was quoted is exactly what we apply, and the 409 guard only fires on a genuine
      // change between preview and commit, never on stale DB/Stripe drift.
      if (preview) {
        try {
          await setWorkspaceSeats(
            workspaceId,
            target,
            preview.currentQuantity,
            preview.prorationDate,
          );
        } catch (err) {
          const code = apiErrorCode(err);
          if (code === 'seats_out_of_sync') {
            staleWorkspaceSeats = true;
            failures.push(
              'Your seat count changed. We refreshed the latest figures, review and confirm.',
            );
          } else if (code === 'seat_payment_failed') {
            failures.push('Payment failed, so workspace seats were not changed.');
          } else {
            failures.push('Unable to update workspace seats. Please try again.');
          }
        }
      }
    }

    if (aiSeatsChanged && aiTarget !== null) {
      try {
        const summary = await updateSpecSeatQuantity(workspaceId, {
          quantity: aiTarget,
          effective: 'immediate_add_or_period_end_reduce',
        });
        // The route returns the post-write summary and this used to throw it
        // away. `onCommitted` refreshes WORKSPACE billing, not Spec billing, so
        // the `spec-billing` cache kept the pre-write quantity -- and reopening
        // the dialog seeded the AI stepper from it. Writing the response into
        // the cache the section reads makes the next open correct without a
        // round trip, and it is the most authoritative value there is: it came
        // from the transaction that did the work.
        queryClient.setQueryData(['spec-billing', workspaceId], summary);
      } catch (err) {
        failures.push(specBillingSaveMessage(err, 'Unable to update AI seats. Please try again.'));
      }
    }

    // Runs whatever happened: a half that landed has to reach the page even
    // when the other half failed, or the manager is looking at stale numbers
    // while deciding whether to retry.
    await onCommitted();

    if (failures.length === 0) {
      onOpenChange(false);
    } else {
      setError(failures.join(' '));
      if (staleWorkspaceSeats) {
        setPreview(null);
        setReloadKey((key) => key + 1);
      }
    }
    setSaving(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Manage seats</DialogTitle>
          <DialogDescription>
            {canManageWorkspaceSeats
              ? `Your plan includes ${includedSeats} seats. Extra seats are billed monthly and prorated from today.`
              : 'AI seats are billed monthly and prorated from today.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/*
            Absent, not disabled, on a plan that cannot buy extra seats. A greyed
            stepper reads as "you have this and it is broken"; the honest state
            is that this half of the dialog does not apply.
          */}
          {canManageWorkspaceSeats ? (
            <div className="flex items-center justify-between rounded-md border px-4 py-3">
              <div>
                <p className="text-sm font-medium">Extra seats</p>
                <p className="text-xs text-muted-foreground">
                  {includedSeats + target} seats total
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={saving || target <= 0}
                  onClick={() => {
                    userAdjustedRef.current = true;
                    setTarget((value) => Math.max(0, value - 1));
                  }}
                  aria-label="Remove a seat"
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-6 text-center text-base font-medium tabular-nums">{target}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  disabled={saving || target >= MAX_EXTRA_SEATS}
                  onClick={() => {
                    userAdjustedRef.current = true;
                    setTarget((value) => Math.min(MAX_EXTRA_SEATS, value + 1));
                  }}
                  aria-label="Add a seat"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ) : null}

          <SpecSeatQuantitySection
            workspaceId={workspaceId}
            target={aiTarget}
            onTargetChange={handleAiTargetChange}
            onBaselineLoaded={handleAiBaseline}
            disabled={saving}
          />

          {!canManageWorkspaceSeats ? null : previewError && !loadingPreview ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-400">
              {previewErrorMessage(previewError)}
            </div>
          ) : (
            <div className="space-y-1 rounded-md border bg-muted/40 px-4 py-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Due today</span>
                {loadingPreview ? (
                  <Skeleton className="h-4 w-16" data-testid="seat-preview-skeleton" />
                ) : (
                  <span className="font-medium tabular-nums">{dueLabel}</span>
                )}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">New monthly total</span>
                {loadingPreview || newMonthlyTotalCents === null ? (
                  <Skeleton className="h-4 w-24" />
                ) : (
                  <span className="tabular-nums">{`${formatMoney(newMonthlyTotalCents, currency)}/mo`}</span>
                )}
              </div>
            </div>
          )}

          {canManageWorkspaceSeats && dueTodayCents < 0 && !loadingPreview && !previewError ? (
            <p className="text-xs text-muted-foreground">
              Reducing seats applies a credit to your next invoice.
            </p>
          ) : null}

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={commit}
              disabled={isCommitDisabled({
                saving,
                loadingPreview,
                noChange,
                previewReady,
                workspaceSeatsChanged,
              })}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {primaryLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
